import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Eye, Download, Copy, Trash, FileText, FilePdf, FileHtml,
  Package, CheckCircle, CaretDown, CaretUp, Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { downloadBlob as safeDownloadBlob } from '@/utils/downloadBlob';

// Lazy loading thumbnail component
const ThumbnailImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  return (
    <div className={`relative ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-slate-700 animate-pulse rounded-lg" />
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`w-full h-full object-cover rounded-lg transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {error && (
        <div className="absolute inset-0 bg-slate-700 rounded-lg flex items-center justify-center">
          <FileText size={24} className="text-slate-500" />
        </div>
      )}
    </div>
  );
};

// File type configurations
const FILE_TYPE_CONFIG = {
  pdf: { icon: FilePdf, color: 'text-red-400', bgColor: 'bg-red-500/20', label: 'PDF' },
  text: { icon: FileText, color: 'text-slate-400', bgColor: 'bg-slate-500/20', label: 'TXT' },
  html: { icon: FileHtml, color: 'text-blue-400', bgColor: 'bg-blue-500/20', label: 'HTML' },
  html_template: { icon: FileHtml, color: 'text-blue-400', bgColor: 'bg-blue-500/20', label: 'HTML' },
  assembled: { icon: FileHtml, color: 'text-green-400', bgColor: 'bg-green-500/20', label: 'FINAL' },
  zip: { icon: Package, color: 'text-purple-400', bgColor: 'bg-purple-500/20', label: 'ZIP' }
};

// Individual output card for package files
const OutputThumbnailCard = ({ output, onPreview, onDownload, onCopy }) => {
  const config = FILE_TYPE_CONFIG[output.type] || FILE_TYPE_CONFIG.text;
  const Icon = config.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-slate-600 transition-all group"
      data-testid={`output-thumb-${output.filename}`}
    >
      {/* Thumbnail area */}
      <div className="aspect-square relative overflow-hidden bg-slate-900">
        {output.thumbnail_url ? (
          <ThumbnailImage 
            src={output.thumbnail_url} 
            alt={output.filename}
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
            <Icon size={40} className={config.color} weight="duotone" />
          </div>
        )}
        
        {/* File type badge */}
        <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
          {config.label}
        </div>
        
        {/* Editable badge */}
        {output.editable && (
          <div className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300">
            Editable
          </div>
        )}
        
        {/* Hover overlay with actions */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            onClick={() => onPreview?.(output)}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            title="Preview"
          >
            <Eye size={18} className="text-white" />
          </button>
          <button
            onClick={() => onDownload?.(output)}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            title="Download"
          >
            <Download size={18} className="text-white" />
          </button>
          {output.editable && (
            <button
              onClick={() => onCopy?.(output)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              title="Copy"
            >
              <Copy size={18} className="text-white" />
            </button>
          )}
        </div>
      </div>
      
      {/* Card info */}
      <div className="p-2.5">
        <p className="text-white text-xs font-medium truncate" title={output.filename}>
          {output.filename}
        </p>
        <p className="text-slate-400 text-xs truncate mt-0.5" title={output.description}>
          {output.description}
        </p>
      </div>
    </motion.div>
  );
};

// Business Package Card with visual thumbnails
const BusinessPackageCard = ({ 
  item, 
  expanded, 
  onToggle, 
  onPreview,
  onDownload,
  onCopy,
  onDelete,
  formatDate,
  planTier = 'pro'
}) => {
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Get outputs from item content or use defaults
  const outputs = item.content?.outputs || item.outputs || [];
  
  // Filter outputs by tier
  const filterOutputsByTier = (outputs) => {
    if (planTier === 'pro') return outputs;
    if (planTier === 'growth') return outputs.filter(o => o.type !== 'assembled');
    return outputs.filter(o => ['pdf', 'text'].includes(o.type));
  };
  
  const visibleOutputs = filterOutputsByTier(outputs);
  const hasOutputs = visibleOutputs.length > 0;
  
  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this package?')) return;
    
    setDeleting(true);
    try {
      await api.delete(`/creations/${item.id}`);
      toast.success('Package deleted');
      onDelete?.(item.id);
    } catch (error) {
      toast.error('Failed to delete package');
    } finally {
      setDeleting(false);
    }
  };
  
  const handleDownloadZip = async () => {
    setDownloading(true);
    try {
      // Get the auth token
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Check if we're on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        // For mobile: Open the download URL directly in a new tab
        // This lets the native browser handle the download
        const downloadUrl = `${baseUrl}/api/business-package/download/${item.id}?token=${token}`;
        window.open(downloadUrl, '_blank');
        toast.success('Download started! Check your Downloads folder.');
      } else {
        // For desktop/iOS: Use blob approach (iOS-Safari-safe)
        const response = await api.get(`/business-package/download/${item.id}`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/octet-stream' });
        const res = await safeDownloadBlob(blob, 'BusinessPackage.zip');
        if (res.ios) {
          toast.success("Package opened — tap Share → Save to Files.", { duration: 6000 });
        } else {
          toast.success('Package downloaded!');
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download package');
    } finally {
      setDownloading(false);
    }
  };
  
  return (
    <div 
      className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden"
      data-testid={`package-card-${item.id}`}
    >
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-slate-800/70 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {/* Main thumbnail */}
            <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
              {item.thumbnail_url || item.content?.thumbnail_url ? (
                <ThumbnailImage 
                  src={item.thumbnail_url || item.content?.thumbnail_url} 
                  alt={item.title || 'Business Package'}
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                  <Package size={24} className="text-white" weight="fill" />
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium">{item.title || 'Business Package'}</h3>
              <p className="text-slate-400 text-sm truncate">
                {item.content?.business_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Business'} Package
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-slate-500">
                  {outputs.length || item.content?.files?.length || 0} files
                </span>
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                  {item.plan_tier?.toUpperCase() || 'PRO'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Right side */}
          <div className="flex items-center gap-3">
            <span className="text-slate-500 text-xs">{formatDate(item.created_at)}</span>
            
            {/* Delete button */}
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(); }}
              disabled={deleting}
              className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
              title="Delete"
            >
              <Trash size={16} />
            </button>
            
            {/* Expand indicator */}
            <motion.div
              animate={{ rotate: expanded ? 180 : 0 }}
              className="text-slate-400"
            >
              <CaretDown size={18} />
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Expanded outputs grid */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-700/50"
          >
            <div className="p-4">
              {hasOutputs ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {visibleOutputs.map((output, idx) => (
                    <OutputThumbnailCard
                      key={`${output.filename}-${idx}`}
                      output={output}
                      onPreview={onPreview}
                      onDownload={onDownload}
                      onCopy={onCopy}
                    />
                  ))}
                </div>
              ) : (
                /* Fallback to file list if no structured outputs */
                item.content?.files && (
                  <div className="space-y-2">
                    <p className="text-slate-400 text-xs mb-2">Package files:</p>
                    <div className="flex flex-wrap gap-2">
                      {item.content.files.map((file, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-slate-700/50 text-slate-300 text-xs rounded-lg flex items-center gap-1.5">
                          <FileText size={12} />
                          {file}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              )}
              
              {/* Quick actions for entire package */}
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-700/50">
                {/* Round 58 — reopen the COMPLETE saved package
                    (all editable fields + PDF + HTML + media URLs)
                    in CreationDetailModal without re-generation. */}
                <button
                  onClick={() => onPreview?.({ id: item.id })}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg flex items-center gap-2"
                  data-testid={`package-card-reopen-${item.id}`}
                >
                  <Eye size={16} />
                  Reopen Full
                </button>
                {(planTier === 'pro' || planTier === 'internal') && (
                  <button
                    onClick={handleDownloadZip}
                    disabled={downloading}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg flex items-center gap-2"
                  >
                    {downloading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download size={16} />
                        Download ZIP
                      </>
                    )}
                  </button>
                )}
                <span className="text-slate-500 text-xs ml-2">
                  Created {formatDate(item.created_at)}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { BusinessPackageCard, OutputThumbnailCard, ThumbnailImage };
export default BusinessPackageCard;
